import React, { useState, useEffect } from 'react';
import { AdaptiveAlertDialog } from '@/components/ui/AdaptiveAlertDialog';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { supabase } from '@/integrations/supabase/client';

interface DeleteCompositionDialogProps {
  composition: MealCompositionWithRecipes | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (composition: MealCompositionWithRecipes) => void;
  isLoading?: boolean;
}

const DeleteCompositionDialog: React.FC<DeleteCompositionDialogProps> = ({
  composition,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const [mealPlanCount, setMealPlanCount] = useState<number>(0);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);
  
  const getDescription = () => {
    if (!composition) return '';
    
    const parts = [
      `Are you sure you want to delete the meal composition "${composition.name}"?`,
      `This composition contains ${composition.recipes.length} recipe${composition.recipes.length !== 1 ? 's' : ''}. Deleting this composition will not affect the individual recipes.`
    ];
    
    if (isCheckingUsage) {
      parts.push('Checking meal plan usage...');
    } else if (mealPlanCount > 0) {
      parts.push(`⚠️ This composition is currently used in ${mealPlanCount} meal plan${mealPlanCount !== 1 ? 's' : ''}. Deleting it will also remove it from your meal planning calendar.`);
    } else {
      parts.push('This composition is not currently used in any meal plans.');
    }
    
    parts.push('This action cannot be undone.');
    return parts.join('\n\n');
  };
  
  useEffect(() => {
    const checkMealPlanUsage = async () => {
      if (!composition || !isOpen) {
        setMealPlanCount(0);
        return;
      }

      setIsCheckingUsage(true);
      try {
        const { count, error } = await supabase
          .from("meal_plans")
          .select("*", { count: 'exact', head: true })
          .eq("meal_composition_id", composition.id);

        if (error) {
          console.error('Error checking meal plan usage:', error);
          setMealPlanCount(0);
        } else {
          setMealPlanCount(count || 0);
        }
      } catch (error) {
        console.error('Error checking meal plan usage:', error);
        setMealPlanCount(0);
      } finally {
        setIsCheckingUsage(false);
      }
    };

    checkMealPlanUsage();
  }, [composition, isOpen]);
  
  if (!composition) return null;

  return (
    <AdaptiveAlertDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      title="Delete Meal Composition"
      description={getDescription()}
      confirmText={isLoading ? 'Deleting...' : 'Delete Composition'}
      onConfirm={() => onConfirm(composition)}
      isLoading={isLoading || isCheckingUsage}
      isDestructive={true}
    />
  );
};

export default DeleteCompositionDialog;